import { HugeiconsIcon } from "@hugeicons/react";
import { dirOf, useLang } from "../i18n/lang";
import {
  Album02Icon,
  Alert02Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowDown01Icon,
  Attachment01Icon,
  Calendar03Icon,
  Cancel01Icon,
  Certificate01Icon,
  Coins01Icon,
  Copy01Icon,
  Delete02Icon,
  Download04Icon,
  DocumentAttachmentIcon,
  FilterHorizontalIcon,
  Grid2X2Icon,
  GridViewIcon,
  ListViewIcon,
  Image01Icon,
  ImageNotFound01Icon,
  InformationCircleIcon,
  LinkSquare02Icon,
  Loading03Icon,
  QrCodeIcon,
  Refresh01Icon,
  Note01Icon,
  PaintBoardIcon,
  PaletteIcon,
  PencilEdit02Icon,
  PlusSignIcon,
  Route01Icon,
  RulerIcon,
  Search01Icon,
  Share08Icon,
  StarIcon,
  Store01Icon,
  Tag01Icon,
  Tick02Icon,
  Undo02Icon,
  User03Icon,
  Video01Icon,
} from "@hugeicons/core-free-icons";

/**
 * Every glyph in this app comes from here, so "is that a Hugeicon?" is a
 * question with one place to check. Names are affordances, not icon names.
 */
export const icons = {
  works: Album02Icon,
  browse: GridViewIcon,
  add: PlusSignIcon,
  search: Search01Icon,
  filter: FilterHorizontalIcon,
  viewGrid: Grid2X2Icon,
  viewList: ListViewIcon,
  edit: PencilEdit02Icon,
  delete: Delete02Icon,
  close: Cancel01Icon,
  check: Tick02Icon,
  undo: Undo02Icon,
  chevronDown: ArrowDown01Icon,
  /** Written for RTL; `Icon` mirrors these two when the document is LTR. */
  forward: ArrowLeft01Icon,
  back: ArrowRight01Icon,
  artist: User03Icon,
  type: PaintBoardIcon,
  year: Calendar03Icon,
  dimensions: RulerIcon,
  medium: PaletteIcon,
  style: Tag01Icon,
  ownership: Tag01Icon,
  valuation: Coins01Icon,
  condition: Note01Icon,
  provenance: Route01Icon,
  authenticity: Certificate01Icon,
  image: Image01Icon,
  /** Marks the one media file a record is represented by. */
  thumbnail: StarIcon,
  noImage: ImageNotFound01Icon,
  video: Video01Icon,
  file: DocumentAttachmentIcon,
  attach: Attachment01Icon,
  info: InformationCircleIcon,
  alert: Alert02Icon,
  spinner: Loading03Icon,
  qr: QrCodeIcon,
  copy: Copy01Icon,
  share: Share08Icon,
  openLink: LinkSquare02Icon,
  download: Download04Icon,
  refresh: Refresh01Icon,
  catalog: Store01Icon,
} as const;

export type IconName = keyof typeof icons;

/**
 * An arrow means "onward", not "leftward". These two are the only glyphs in
 * the set that have to mirror when the writing direction flips.
 */
const MIRRORED: Partial<Record<IconName, IconName>> = {
  forward: "back",
  back: "forward",
};

/** 16 beside text · 20 chrome · 24 bottom tabs — the sizes DESIGN.md fixes. */
export type IconSize = 16 | 20 | 24 | 32 | 48;

interface IconProps {
  name: IconName;
  size?: IconSize;
  className?: string;
  /** Set only on an icon that is the sole content of a control. */
  label?: string;
}

export function Icon({ name, size = 20, className, label }: IconProps) {
  const rtl = dirOf(useLang()) === "rtl";
  const resolved = rtl ? name : (MIRRORED[name] ?? name);
  return (
    <HugeiconsIcon
      icon={icons[resolved]}
      size={size}
      color="currentColor"
      strokeWidth={1.75}
      className={className}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
    />
  );
}
